import './style.scss';
import { BdsBanner, BdsGrid, BdsUpload } from 'blip-ds/dist/blip-ds-react';
import { BdsUploadCustomEvent } from 'blip-ds';
import { useEffect, useState } from 'react';
import { CREATE_TEMPLATE_MAX_IMAGE_SIZE } from 'configurations/Environment';
import { TemplateType } from 'objects/types/MessageTemplate';
import { Analytics } from 'infra/adapters';

type IActiveMessagesCreateTemplateUploadFileProps = {
  templateType: TemplateType;
  onChangeFile: (file: File | undefined) => void;
};

export const ActiveMessagesCreateTemplateUploadFile = ({
  templateType,
  onChangeFile,
}: IActiveMessagesCreateTemplateUploadFileProps) => {
  const IMAGE_FILE_TYPES = ['image/jpeg', 'image/png'];
  const [uploadErrorMessage, setUploadErrorMessage] = useState('');
  const [fileTypes, setFileTypes] = useState<Array<string>>([]);
  const [formatType, setFormatType] = useState('');
  const [maxFileSize, setMaxFileSize] = useState(0);
  const [subtitle, setSubtitle] = useState('');
  const [acceptedFileExtensions, setAcceptedFileExtensions] = useState('');
  const [fileDataUrl, setFileDataUrl] = useState<string>();

  useEffect(() => {
    switch (templateType) {
      case TemplateType.Image:
        setFileTypes(IMAGE_FILE_TYPES);
        setFormatType('jpg, jpeg ou png');
        setMaxFileSize(Number(CREATE_TEMPLATE_MAX_IMAGE_SIZE) ?? 0);
        setSubtitle(
          `Envie mídias em JPG, JPEG ou PNG, com até ${Number(CREATE_TEMPLATE_MAX_IMAGE_SIZE) / 1024 / 1024}MB`,
        );
        setAcceptedFileExtensions('.jpg, .jpeg, .png');
        break;
    }
  }, [templateType]);

  const onUploadChange = (event: BdsUploadCustomEvent<{ value: File[] }>) => {
    const file = event.detail.value[0];
    setUploadErrorMessage('');
    if (!file) {
      return;
    }

    if (!fileTypes.includes(file.type)) {
      setUploadErrorMessage(`Por favor, selecione um arquivo em formato ${formatType}`);
      return;
    }

    if (file.size > maxFileSize) {
      setUploadErrorMessage(
        `O arquivo enviado ultrapassa o limite permitido de ${maxFileSize / 1024 / 1024}MB. Tente novamente.`,
      );
      return;
    }

    onChangeFile(file);
    Analytics.Track(Analytics.events.ACTIVE_MESSAGES_CREATE_TEMPLATE_UPLOAD_MIDIA, {
      type: TemplateType[templateType],
    });

    const fileReader = new FileReader();
    fileReader.onload = event => onUploadFileReaderOnload(event);
    fileReader.readAsDataURL(file);
  };

  const onUploadFileReaderOnload = (event: ProgressEvent<FileReader>) => {
    const dataUrl = event.target?.result;

    if (dataUrl) {
      setFileDataUrl(dataUrl as any);
    }
  };

  const onUploadDelete = () => {
    setUploadErrorMessage('');
    onChangeFile(undefined);
    setFileDataUrl(undefined);
  };

  return (
    <BdsGrid className="active-messages-create-template-upload-file" direction="column" gap="2" margin="t-2">
      <BdsUpload
        data-testid="upload-template-file"
        titleName="Carregar mídia"
        subtitle={subtitle}
        accept={acceptedFileExtensions}
        onBdsUploadChange={onUploadChange}
        onBdsUploadDelete={onUploadDelete}
      />
      {uploadErrorMessage && (
        <BdsBanner variant="warning" button-close="true" context="inside">
          {uploadErrorMessage}
        </BdsBanner>
      )}
      {templateType === TemplateType.Image && fileDataUrl && (
        <img className="image-upload-file" src={fileDataUrl} alt="Uploaded image" />
      )}
    </BdsGrid>
  );
};
